import { Button } from '@repo/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@repo/ui/components/ui/dropdown-menu';
import { Textarea } from '@repo/ui/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components/ui/tooltip';
import { useScreenSize } from '@repo/ui/hooks';
import { cn } from '@repo/ui/lib/utils';
import EmojiPicker from 'emoji-picker-react';
import { Info, Smile, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SelfCommentModel } from './self-comment';
import allowEditSelfComment from '@/helpers/allow-edit-self-comment';
import { CommentActions } from '@/components/candidates/comment.action';

export type CommentProps = {
  title?: string;
  tooltipText?: string;
  saveButtonText?: string;
  saveButtonIcon?: React.ReactNode;
  cancelButtonText?: string;
  processing?: boolean;
  rating?: number;
  userName: string;
  userComment?: SelfCommentModel;
  isCommentClosed: boolean;
  useTooltip?: boolean;
  onCommentAddClick: (rating: number, comment?: string) => void;
  onCancelClick: (rating: number) => void;
  onUpdateClick: (commentId: string, comment: string) => void;
};

const Comment = ({
  title = 'Add A comment as:',
  tooltipText = 'Adding a comment is optional but you can use it to provide extra detail to your score.',
  saveButtonText = 'Add',
  cancelButtonText = 'Skip',
  saveButtonIcon,
  onCommentAddClick,
  onCancelClick,
  onUpdateClick,
  processing,
  rating,
  userName,
  userComment,
  isCommentClosed,
  useTooltip = true,
}: CommentProps) => {
  const { isMedium, isSmall } = useScreenSize();
  const [comment, setComment] = useState<string>(userComment?.comment || '');
  const [isTooltipOpen, setTooltipOpen] = useState(false);
  const isAllowEditComment = userComment
    ? allowEditSelfComment(userComment.createdAt)
    : true;

  useEffect(() => {
    setComment(userComment?.comment || '');
  }, [userComment]);

  return (
    <div
      className={cn('grid gap-2', {
        hidden: isCommentClosed,
      })}
    >
      <div className="flex flex-col">
        <div className="flex justify-between items-center px-2 pt-2">
          <div>
            <span className="uppercase text-xs font-light">{title} </span>{' '}
            <span className="text-xs font-semibold">{userName}</span>
          </div>
          {useTooltip && (
            <TooltipProvider delayDuration={200}>
              <Tooltip open={isTooltipOpen}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="hover:bg-transparent p-0"
                    onClick={() => setTooltipOpen(!isTooltipOpen)}
                  >
                    <Info size={16} className="cursor-pointer" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  className="bg-white p-0 max-w-[22.6875rem] text-sm font-normal text-cn-foreground shadow-lg rounded-md relative"
                  onPointerDownOutside={() => setTooltipOpen(false)}
                  side="left"
                  sideOffset={-16}
                  align="start"
                  alignOffset={36}
                >
                  {(isMedium || isSmall) && (
                    <button
                      onClick={() => setTooltipOpen(false)}
                      className="absolute top-2 right-2 hover:bg-gray-100 rounded-full"
                      aria-label="Close tooltip"
                    >
                      <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                  <p className="p-5">{tooltipText}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="relative">
          <Textarea
            value={comment}
            onInput={(e) => setComment(e.currentTarget.value)}
            className="pr-10 min-h-[80px] text-base"
          />
          <div className="absolute right-4 bottom-4 text-gray-400 cursor-pointer hover:text-gray-600">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Smile size={16} />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <EmojiPicker
                  onEmojiClick={(emoji) => {
                    setComment((prev) => prev + emoji.emoji);
                  }}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      {!userComment && (
        <CommentActions
          saveButtonText={saveButtonText}
          cancelButtonText={cancelButtonText}
          saveButtonIcon={saveButtonIcon}
          onSaveClick={() => onCommentAddClick(rating!, comment)}
          onCancelClick={() => onCancelClick(rating!)}
          processing={processing}
          disabled={!isAllowEditComment}
        />
      )}
      {userComment && (
        <CommentActions
          onSaveClick={() => onUpdateClick(userComment.commentId, comment)}
          onCancelClick={() => onCancelClick(rating!)}
          processing={processing}
          disabled={!isAllowEditComment}
        />
      )}
    </div>
  );
};

export default Comment;
